import { profileById, type ProfileMeta } from '@/data/catalog';
import { t } from '@/i18n';
import { formatClock } from '@/lib/time';
import type { Message, MessageStatus, Opinion, RoundType } from '@/types';

export interface ImageVM {
  id: string;
  label: string;
  thumbUrl?: string;
}

export interface UserTurnVM {
  text: string;
  time: string;
  images: ImageVM[];
}

export interface CardVM {
  messageId: string;
  profile: ProfileMeta;
  status: MessageStatus;
  opinions: Opinion[]; // unfiltered
  partial?: string;
  error?: string;
}

export interface RoundVM {
  round: number;
  type: RoundType;
  user?: UserTurnVM;
  cards: CardVM[];
}

/** Groups a thread's messages into rounds; cards follow the thread's checked order, not completion order. */
export function buildRounds(messages: Message[], profileIds: string[], resolveImage: (id: string) => ImageVM): RoundVM[] {
  const rounds = new Map<number, RoundVM>();
  for (const m of [...messages].sort((a, b) => a.seq - b.seq)) {
    let round = rounds.get(m.round);
    if (!round) {
      round = { round: m.round, type: m.roundType, cards: [] };
      rounds.set(m.round, round);
    }
    if (m.role === 'user') {
      round.user = { text: m.text ?? '', time: formatClock(m.createdAt), images: (m.imageIds ?? []).map(resolveImage) };
      continue;
    }
    const profile = m.profileId ? profileById(m.profileId) : undefined;
    if (!profile) continue;
    round.cards.push({ messageId: m.id, profile, status: m.status, opinions: m.opinions ?? [], partial: m.raw, error: m.error });
  }
  const order = new Map(profileIds.map((id, i) => [id, i]));
  const rank = (c: CardVM) => order.get(c.profile.id) ?? Number.MAX_SAFE_INTEGER;
  for (const r of rounds.values()) r.cards.sort((a, b) => rank(a) - rank(b));
  return [...rounds.values()].sort((a, b) => a.round - b.round);
}

export function roundLabel(round: RoundVM): string {
  return t('experts.round', { n: round.round, type: t(round.type === 'debate' ? 'experts.debate' : 'experts.answer') });
}
